import { Either, Right } from "purify-ts/Either";
import { formatDate } from "../utils/dateValidationMsg";
import { Field } from "@nvdb-rapport/shared/src/components/formTypes";
import { VegSystem } from "@nvdb-rapport/shared/src/utils/vegfilter";
import { Fylke, Kommune } from "@nvdb-rapport/shared/src/components/AreaSelect";
import * as R from "remeda";

function initialField<V>(value: V): Field<V> {
  return { value, touched: false, dirty: false };
}

export interface Bruksklasse {
  id: number;
  navn: string;
  offisiell: boolean;
  heldekkende: boolean;
}

export interface ArbeidModel {
  bruksklasser: Bruksklasse[];
  selectedBruksklasse: Field<Bruksklasse | undefined>;
  selectedAreaType: "fylker" | "kommuner" | undefined;
  selectedFylker: Field<Fylke[]>;
  selectedKommuner: Field<Kommune[]>;
  vegsystemInput: string;
  vegsystem: Either<string, VegSystem>;
  date: Either<string, string>;
  errorAreaType: boolean;
  errorAreaSelect: boolean;
  errorVegsystemRef: boolean;
  kommuneOptions: Map<string, number>;
  selectedReadVersion: Field<boolean | undefined>;
}

export const initialArbeidModel: ArbeidModel = {
  bruksklasser: [],
  selectedAreaType: undefined,
  selectedBruksklasse: initialField(undefined),
  selectedFylker: initialField([]),
  selectedKommuner: initialField([]),
  kommuneOptions: new Map<string, number>(),
  vegsystemInput: "",
  vegsystem: Right([]),
  date: Right(formatDate(new Date())),
  errorAreaType: false,
  errorAreaSelect: false,
  errorVegsystemRef: false,
  selectedReadVersion: initialField(true),
};

type SetBruksklasser = { type: "SetBruksklasser"; payload: Bruksklasse[] };
type SelectBruksklasse = {
  type: "SelectBruksklasse";
  payload: Field<Bruksklasse | undefined>;
};
type UpdateAreaType = {
  type: "UpdateAreaType";
  payload: "fylker" | "kommuner";
};

type SetSelectedKommuner2 = {
  type: "SetSelectedKommuner2";
  payload: Field<Kommune[]>;
};

type SetSelectedFylker2 = {
  type: "SetSelectedFylker2";
  payload: Field<Fylke[]>;
};

type SelectDate = { type: "SelectDate"; payload: Either<string, string> };
type UpdateVegsystem = { type: "UpdateVegsystem"; payload: string };
type SetAreaTypeError = { type: "SetAreaTypeError"; payload: boolean };
type SetAreaTypeSelectError = {
  type: "SetAreaTypeSelectError";
  payload: boolean;
};
type SetVegsystemRefError = { type: "SetVegsystemRefError"; payload: boolean };
type SetKommuneOptions = {
  type: "SetKommuneOptions";
  payload: Map<string, number>;
};
type Touch = { type: "Touch"; payload: keyof ArbeidModel };

type SelectReadversion = {
  type: "SelectReadversion";
  payload: Field<boolean | undefined>;
};

export type ArbeidAction =
  | SetBruksklasser
  | SelectBruksklasse
  | UpdateAreaType
  | SelectDate
  | UpdateVegsystem
  | SetAreaTypeError
  | SetVegsystemRefError
  | SetAreaTypeSelectError
  | SetVegsystemRefError
  | SetAreaTypeSelectError
  | SetKommuneOptions
  | Touch
  | SetSelectedKommuner2
  | SetSelectedFylker2
  | SelectReadversion;

export function arbeidReducer(
  model: ArbeidModel,
  action: ArbeidAction
): ArbeidModel {
  switch (action.type) {
    case "SetBruksklasser": {
      return { ...model, bruksklasser: action.payload };
    }
    case "SelectBruksklasse": {
      return R.pipe(model, R.set("selectedBruksklasse", action.payload));
    }
    case "UpdateAreaType": {
      return {
        ...model,
        selectedAreaType: action.payload,
        selectedFylker: initialArbeidModel.selectedFylker,
        selectedKommuner: initialArbeidModel.selectedKommuner,
      };
    }

    case "SelectDate": {
      return { ...model, date: action.payload };
    }
    case "UpdateVegsystem": {
      return {
        ...model,
        vegsystemInput: action.payload,
        vegsystem: VegSystem.decode(action.payload.split(",")),
      };
    }
    case "SetAreaTypeError": {
      return { ...model, errorAreaType: action.payload };
    }
    case "SetVegsystemRefError": {
      return { ...model, errorVegsystemRef: action.payload };
    }
    case "SetAreaTypeSelectError": {
      return { ...model, errorAreaSelect: action.payload };
    }
    case "SetKommuneOptions": {
      return { ...model, kommuneOptions: action.payload };
    }
    case "Touch":
      if (action.payload === "selectedBruksklasse") {
        return {
          ...model,
          [action.payload]: { ...model[action.payload], touched: true },
        };
      } else {
        return { ...model };
      }
    case "SetSelectedFylker2":
      return R.pipe(model, R.set("selectedFylker", action.payload));
    case "SetSelectedKommuner2":
      return R.pipe(model, R.set("selectedKommuner", action.payload));
    case "SelectReadversion":
      return R.pipe(model, R.set("selectedReadVersion", action.payload));
  }
}

export type ArbeidStateProps = { arbeidState: ArbeidModel };
export type ArbeidDispatchProps = {
  arbeidDispatch: React.Dispatch<ArbeidAction>;
};

export type ArbeidProps = ArbeidStateProps & ArbeidDispatchProps;
